import { type ProductTransactionData } from '@unisporkal/ga4';
import { type CartItem } from 'types/isomorphic/cart.types';
import { type CustomerDownloadAgreement } from 'types/isomorphic';
import { type Asset } from '../types/assetType';
import { type TrackingDataAssetDetails } from '../types/trackingData';
import acquisitionTypes, { type AcquisitionType } from './acquisitionTypes';

export const trackingDownloadType = (
  agreementOptionType: string,
  agreementSubFamily?: string
): string | null => {
  switch (agreementOptionType) {
    case 'EasyAccess':
      return 'EZA';
    case 'PremiumAccess':
      return 'PA';
    case 'EditorialSubscription':
      return 'EdSub';
    case 'ImagePack':
      return 'RFPack';
    case 'RoyaltyFreeSubscription':
      return agreementSubFamily === 'editorial'
        ? 'gi_ecomm_sub_ed'
        : 'gi_ecomm_sub_cr';
    default:
      return null;
  }
};

export const trackingAgreementDownloadTypes = (
  agreements: CustomerDownloadAgreement[]
): (string | null)[] =>
  // Filter out RoyaltyFreeSubscription agreements as they are not tracking method_download
  // for batch downloads on the rfSubs
  agreements
    .filter((agreement) => agreement.productType !== 'RoyaltyFreeSubscription')
    .map((agreement) =>
      trackingDownloadType(agreement.productType, agreement.subFamily)
    );

export const trackingAsset = (asset: Asset, sizeName: string) => ({
  asset_id: asset.id,
  asset_family: asset.family,
  asset_size: sizeName,
  asset_type: asset.assetType,
  asset_usage: asset.family,
  date_submitted: asset.dateSubmitted,
  collection_code: asset.collectionCode,
  license_type: asset.licenseType,
  media_type: asset.mediaType,
  quality: asset.quality,
  release_info: asset.releaseCode,
});

export const limitedUseTrackingSkus = {
  SocialMedia: 'limited_use-social_media',
  Internal: 'limited_use-internal',
  SocialMediaPlusOneDigital: 'limited_use-digital',
};

export const getAcquisitionLocationId = (
  acquisitionType: AcquisitionType
): string | undefined => {
  if (
    [acquisitionTypes.CART_ADP_CONFIRMATION, acquisitionTypes.LICENSE].includes(
      acquisitionType
    )
  ) {
    return 'adp_size_buy_card';
  }

  if (acquisitionType === acquisitionTypes.ALACARTE_LICENSE) {
    return 'adp_buy_card_buy_now';
  }

  return acquisitionType || undefined;
};

const getFirstDefined = <T>(...args: (T | null | undefined)[]): T | undefined =>
  args.find((item): item is T => typeof item !== 'undefined' && item !== null);

export function getTrackingItem(
  asset?: Asset,
  cartItem?: Partial<CartItem>
): ProductTransactionData | null {
  const assetDetails: TrackingDataAssetDetails | undefined =
    typeof window === 'undefined'
      ? undefined
      : window.tracking_data?.asset_details;

  if (!(cartItem && 'product' in cartItem) && !asset && !assetDetails) {
    return null;
  }

  return {
    assetId: getFirstDefined(
      cartItem?.product?.assetId,
      assetDetails?.asset_id,
      asset?.id
    ),
    assetFamily: getFirstDefined(
      cartItem?.product?.assetFamily,
      assetDetails?.asset_family,
      asset?.family
    ),
    assetType: getFirstDefined(assetDetails?.asset_type, asset?.assetType),
    collectionCode: getFirstDefined(
      cartItem?.product?.collectionCode,
      assetDetails?.collection_code,
      asset?.collectionCode
    ),
    itemBrand: window?.currentSite?.nickname,
    itemCategory: 'asset',
    discountType: cartItem?.discountType ?? 'None',
    finalPrice: getFirstDefined(cartItem?.finalPrice),
    licenseType: getFirstDefined(
      cartItem?.product?.licenseType,
      assetDetails?.license_type,
      asset?.licenseType
    ),
    mediaType: getFirstDefined(assetDetails?.media_type, asset?.mediaType),
    originalPrice: getFirstDefined(cartItem?.originalPrice),
    releaseInfo: getFirstDefined(
      cartItem?.product?.releaseInfo,
      asset?.releaseCode
    ),
  };
}
